import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getScorecard, type ScoreRow } from "../api/scorecard";

/**
 * The floor manager's performance board: one row per rep for a date range.
 *
 * Built from the board an operator shared on 2026-09-21. Two deliberate
 * departures from it, both because copying it exactly would have meant showing
 * numbers this platform cannot honestly produce:
 *
 *  - "Dials / contacts" becomes OFFERED / TAKEN. This is a predictive dialer:
 *    the system dials and hands the agent a live person, so a per-agent dial
 *    count would be a number with nothing behind it. Offered/taken answers the
 *    question a manager actually has -- when we reach you, do you pick up?
 *
 *  - No BPO-team column yet. Nothing in this database records which agency an
 *    agent belongs to, and a column of blanks is worse than no column. The
 *    field is carried through the API so it can be filled in once that exists.
 */
type PeriodKey = "today" | "yesterday" | "week" | "last_week" | "month" | "last_month";

export default function ScorecardPage() {
  const [period, setPeriod] = useState<PeriodKey>("today");
  const range = periodRange(period);

  const { data, isLoading, error, dataUpdatedAt, refetch, isFetching } = useQuery({
    queryKey: ["scorecard", range.date_from, range.date_to],
    queryFn: () => getScorecard(range),
    // A manager watching the floor wants this to move without pressing things.
    refetchInterval: period === "today" ? 60_000 : false,
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Agent performance</h1>
          <p className="mt-1 text-sm text-gray-500">
            One row per rep. Transfers per hour drives the verdict; dispo
            integrity is the share of answered calls that got a disposition.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 bg-white p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  period === p.key
                    ? "bg-brand text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            {isFetching ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {dataUpdatedAt > 0 && (
        <p className="mb-2 text-xs text-gray-400">
          Data as of{" "}
          {new Date(dataUpdatedAt).toLocaleTimeString([], {
            hour: "2-digit", minute: "2-digit",
          })}
          {data?.meta ? ` · ${data.meta.date_from} to ${data.meta.date_to}` : ""}
        </p>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
          Could not load the board. {(error as Error).message}
        </div>
      )}
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {data && data.rows.length === 0 && !isLoading && (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
          Nobody was on shift in this period.
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2 text-right">Logged</th>
                <th className="px-4 py-2 text-right">Offered / taken</th>
                <th className="px-4 py-2 text-right">Transfers</th>
                <th className="px-4 py-2 text-right" title="Transfers per hour on shift">TPH</th>
                <th className="px-4 py-2 text-right" title="Transfers per day worked">TPD</th>
                <th className="px-4 py-2 text-right">Avg talk</th>
                <th className="px-4 py-2 text-right" title="Calls over 2 minutes">2m+</th>
                <th className="px-4 py-2 text-right" title="Calls over 20 minutes">20m+</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2" title="Share of answered calls that got a disposition">
                  Dispo
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((r) => (
                <Row key={r.agent_name} r={r} />
              ))}
            </tbody>
            {data.totals && (
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                <Row r={data.totals} isTotal />
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ r, isTotal = false }: { r: ScoreRow; isTotal?: boolean }) {
  return (
    <tr className={isTotal ? "" : "hover:bg-gray-50"}>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          {!isTotal && (
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-semibold text-brand"
            >
              {initials(r.agent_name)}
            </span>
          )}
          <div>
            <div className="font-medium text-gray-900">{r.agent_name}</div>
            {!isTotal && r.extension_name && (
              <div className="text-xs text-gray-400">Ext {r.extension_name}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums">
        {hours(r.logged_s)}
        <div className="text-xs text-gray-400">Talk {hours(r.talk_s)}</div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums">
        {r.offered.toLocaleString()} / {r.taken.toLocaleString()}
        <div className={`text-xs ${r.contact_rate < 0.7 ? "text-amber-600" : "text-gray-400"}`}>
          {(r.contact_rate * 100).toFixed(1)}% answered
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{r.transfers}</td>
      <td className="px-4 py-2 text-right tabular-nums">{r.tph.toFixed(2)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{r.tpd.toFixed(1)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{mmss(r.avg_talk_s)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{r.over_2m}</td>
      <td className="px-4 py-2 text-right tabular-nums">{r.over_20m}</td>
      <td className="px-4 py-2">{isTotal ? null : <StatusPill status={r.status} />}</td>
      <td className="px-4 py-2">{isTotal ? null : <Grade r={r} />}</td>
    </tr>
  );
}

function StatusPill({ status }: { status: string }) {
  const style =
    status === "star performer"
      ? "bg-green-100 text-green-800"
      : status === "needs improvement"
        ? "bg-amber-100 text-amber-800"
        : status === "chopping block"
          ? "bg-red-100 text-red-800"
          : "bg-gray-100 text-gray-600";
  return (
    <span className={`whitespace-nowrap rounded px-2 py-0.5 text-xs ${style}`}>
      {status}
    </span>
  );
}

function Grade({ r }: { r: ScoreRow }) {
  const bad = r.grade === "F" || r.grade === "C";
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs tabular-nums ${
        bad ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
      }`}
      title={`${r.dispositions} dispositions across ${r.taken} answered calls`}
    >
      {r.grade} ({(r.dispo_integrity * 100).toFixed(0)}%)
    </span>
  );
}

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "month", label: "This month" },
  { key: "last_month", label: "Last month" },
];

/** A named period as the inclusive LOCAL dates the API filters on.
 *
 * Local, not UTC: a manager asking for "today" means their day, and a call at
 * 6pm Central belongs to today even though it is already tomorrow in UTC.
 * Weeks start Monday — the working week these floors run, not the Sunday
 * getDay() hands you by default.
 */
export function periodRange(p: PeriodKey): { date_from: string; date_to: string } {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  const from = new Date();
  const to = new Date();
  const mondayOffset = (d: Date) => (d.getDay() + 6) % 7;

  if (p === "yesterday") {
    from.setDate(from.getDate() - 1);
    to.setDate(to.getDate() - 1);
  } else if (p === "week") {
    from.setDate(from.getDate() - mondayOffset(from));
  } else if (p === "last_week") {
    from.setDate(from.getDate() - mondayOffset(from) - 7);
    to.setTime(from.getTime());
    to.setDate(to.getDate() + 6);
  } else if (p === "month") {
    from.setDate(1);
  } else if (p === "last_month") {
    from.setDate(1);
    from.setMonth(from.getMonth() - 1);
    to.setDate(0); // last day of the previous month
  }
  return { date_from: iso(from), date_to: iso(to) };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function hours(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function mmss(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
